import { useState, useEffect } from "react";
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { btn } from "@/components/Page";

export function ProfilePictureSection({ userId, isOwnProfile, userName }) {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadStatus = async () => {
    try {
      const res = await api.get("/profile-picture/status");
      setStatus(res.data);
    } catch (e) {
      console.error("Failed to load profile picture status", e);
    }
  };

  useEffect(() => {
    if (isOwnProfile) {
      loadStatus();
    }
  }, [isOwnProfile]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Please select a JPG, PNG, or WebP image");
      return;
    }

    // Validate file size (1 MB)
    if (file.size > 1_000_000) {
      toast.error(`File too large: ${(file.size / 1_000_000).toFixed(2)} MB. Maximum: 1 MB`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await api.post("/profile-picture/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Profile picture uploaded! Awaiting admin approval.");
      setShowUpload(false);
      setSelectedFile(null);
      setPreview(null);
      await loadStatus();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.delete("/profile-picture/cancel-pending");
      toast.success("Upload cancelled");
      await loadStatus();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const handleRemove = async () => {
    if (!confirm("Remove your profile picture?")) return;
    try {
      await api.delete("/profile-picture/remove");
      toast.success("Profile picture removed");
      await loadStatus();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  // Check if viewing own profile
  if (!isOwnProfile) {
    // Show other user's profile picture or placeholder
    return (
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          <div className="aspect-square bg-zinc-900 rounded-sm flex items-center justify-center">
            <div className="text-6xl text-zinc-700">👤</div>
          </div>
        </div>
      </div>
    );
  }

  // Own profile - show based on status
  if (!status) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          <div className="aspect-square bg-zinc-900 rounded-sm flex items-center justify-center">
            <Loader2 className="animate-spin text-zinc-600" size={32} />
          </div>
        </div>
      </div>
    );
  }

  // Not VIP
  if (!status.has_vip) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          <div className="aspect-square border-2 border-dashed border-zinc-800 rounded-sm flex flex-col items-center justify-center p-4">
            <div className="text-3xl mb-2">⭐</div>
            <div className="text-xs text-zinc-500 text-center">
              Upgrade to VIP to set a profile picture
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Feature not enabled
  if (!status.feature_enabled) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          <div className="aspect-square bg-zinc-900 rounded-sm flex items-center justify-center">
            <div className="text-xs text-zinc-600 text-center px-4">
              Profile pictures coming soon
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Has approved picture
  if (status.approved && !showUpload) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          <div className="relative group">
            <img 
              src={status.approved.image_url} 
              alt="Profile" 
              className="w-full aspect-square object-cover rounded-sm"
            />
            <div className="mt-3 flex gap-2">
              <button 
                onClick={() => setShowUpload(true)}
                className={btn("ghost") + " flex-1 text-xs py-2"}
              >
                Change Picture
              </button>
              <button 
                onClick={handleRemove}
                className="px-3 py-2 text-xs border border-red-900/40 text-red-400 hover:bg-red-950/20 rounded-sm transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Has pending picture
  if (status.pending) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-amber-800/40 bg-amber-950/10 p-4 rounded-sm">
          <div className="border-2 border-dashed border-amber-700/40 rounded-sm p-4 aspect-square flex flex-col items-center justify-center">
            <div className="mb-2">⏳</div>
            <div className="text-xs text-amber-300 text-center mb-2">Pending Approval</div>
            <img 
              src={status.pending.thumbnail_url} 
              alt="Preview" 
              className="w-24 h-24 object-cover rounded-sm mb-3 opacity-50"
            />
            <button 
              onClick={handleCancel}
              className="text-[10px] text-zinc-500 hover:text-zinc-400"
            >
              Cancel Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Latest rejected
  if (status.latest_rejected && !showUpload) {
    return (
      <div className="w-64 shrink-0">
        <div className="border border-red-900/40 bg-red-950/10 p-4 rounded-sm">
          <div className="border border-red-800/40 rounded-sm p-4">
            <div className="flex items-center gap-2 text-xs text-red-300 mb-2">
              <AlertCircle size={14} />
              <span>Rejected</span>
            </div>
            <div className="text-[10px] text-zinc-400 mb-3">
              {status.latest_rejected.rejection_reason}
            </div>
            <button 
              onClick={() => setShowUpload(true)}
              className={btn() + " w-full text-xs py-2"}
            >
              Upload New
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No picture - show upload option
  return (
    <>
      <div className="w-64 shrink-0">
        <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
          {!showUpload ? (
            <button 
              onClick={() => setShowUpload(true)}
              className="w-full aspect-square border-2 border-dashed border-zinc-700 rounded-sm flex flex-col items-center justify-center hover:border-amber-700/50 transition-colors"
            >
              <div className="text-3xl mb-2">📸</div>
              <div className="text-xs text-amber-300">Upload Picture</div>
              <div className="text-[10px] text-zinc-600 mt-1">VIP Feature</div>
            </button>
          ) : (
            <div>
              <div className="text-xs text-amber-300 mb-3 font-cinzel">Upload Profile Avatar</div>
              {!selectedFile ? (
                <label className="block cursor-pointer">
                  <div className="aspect-square border-2 border-dashed border-zinc-700 rounded-sm flex flex-col items-center justify-center hover:border-amber-700/50 transition-colors">
                    <Upload size={32} className="text-zinc-600 mb-2" />
                    <div className="text-xs text-zinc-400 text-center px-4">
                      Click to select image
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-2">Max 1 MB · Square recommended</div>
                  </div>
                  <input 
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              ) : (
                <div>
                  <div className="aspect-square bg-zinc-900 rounded-sm mb-3 overflow-hidden">
                    <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-[10px] text-zinc-500 mb-3">
                    ⚠️ Image will be reviewed by admin before appearing on your profile
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleUpload}
                      disabled={uploading}
                      className={btn() + " flex-1 text-xs py-2"}
                    >
                      {uploading ? <><Loader2 size={12} className="animate-spin mr-1" /> Uploading...</> : "Upload"}
                    </button>
                    <button 
                      onClick={() => { setSelectedFile(null); setPreview(null); }}
                      className={btn("ghost") + " px-3 text-xs"}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
              {!selectedFile && (
                <button 
                  onClick={() => setShowUpload(false)}
                  className="w-full mt-2 text-xs text-zinc-500 hover:text-zinc-400 py-1"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
